import { AppState } from "react-native";

/**
 * One rate-limited "the app came to the foreground" signal.
 *
 * ## The problem this exists for
 *
 * On web, `AppState` is not an app lifecycle at all. `react-native-web`'s
 * implementation is a thin wrapper over `document.visibilitychange`
 * (`exports/AppState/index.js`): the tab becoming hidden emits `background`
 * and the tab becoming visible emits `active`. So on the web build, "the app
 * resumed" and "the user glanced at another tab" are literally the same event.
 *
 * Roughly a dozen places subscribe to it, and four of them each start network
 * work: the release/OTA gate, the referral open ping, the plan re-read, and
 * the app-wide resync (which is six requests on its own — venue, inbox, sent
 * cues, chats, group inbox, nearby). That is nine requests per foreground.
 * On a phone that is fine; a person alt-tabbing between the app and another
 * site every few seconds was issuing nine requests every few seconds, for as
 * long as they kept doing it, with only the resync gate's three-second window
 * standing in the way of any of it.
 *
 * ## The rule
 *
 * A named job runs at most once per `minIntervalMs` of foregrounds. Nine
 * requests per flip becomes nine per minute, whatever the user does with their
 * tabs. It is a ceiling, not a schedule: a genuine return after ten minutes
 * away still runs immediately, because the limiter only ever says "not yet",
 * never "not now, come back later".
 *
 * ## What this deliberately does NOT cover
 *
 * Handlers that cost nothing and must stay exact keep subscribing to
 * `AppState` directly: hiding the splash, releasing the keyboard, re-reading a
 * notification permission, flushing an outbox that is usually empty. Delaying
 * those would be a bug, not a saving. The line is "does this foreground issue
 * a request".
 *
 * It is also not a substitute for catching up after a real disconnect. A
 * socket that actually dropped calls its own `onConnect`, which is a separate
 * trigger on a separate (short) window — see `features/realtime/resync-gate.ts`.
 * That is what makes a long window safe here: a hidden web tab keeps its
 * WebSocket, so a short flip missed nothing, and a flip long enough to lose
 * the socket announces itself through the reconnect instead.
 */

import {
  claimForeground,
  markForegroundRan,
} from "./foreground-limiter";

type Options = {
  /** Minimum spacing between runs of this job. Defaults to one minute. */
  minIntervalMs?: number;
  /**
   * The caller has just done this job's work beside the subscription, so the
   * clock should start now rather than at the next foreground.
   *
   * Opt-in, and the default is the safe direction: forgetting it costs one
   * extra run, where assuming it costs a SKIPPED one. See
   * `markForegroundRan` for the call site that proved the assumption wrong.
   */
  justRan?: boolean;
  /** Injectable for tests. */
  now?: () => number;
};

/**
 * Run `handler` when the app comes to the foreground, at most once per
 * interval. Returns an unsubscribe.
 *
 * Pass `justRan` when the caller has already done this work beside the
 * subscription; without it the next foreground runs, which is the direction a
 * mistake should fall.
 *
 * The policy — including why the ledger is module level and timed from the
 * start of a run — lives in `foreground-limiter.ts`.
 */
export function onForeground(
  name: string,
  handler: () => void,
  options: Options = {},
): () => void {
  const now = options.now ?? Date.now;

  if (options.justRan) markForegroundRan(name, now());

  const subscription = AppState.addEventListener("change", (state) => {
    if (state !== "active") return;
    if (!claimForeground(name, now(), options.minIntervalMs)) return;
    handler();
  });

  return () => subscription.remove();
}
